// src/utils/countEligibleVoters.js
import { collection, getDocs, query, where, documentId } from 'firebase/firestore';
import { db } from '../firebase';

const INACTIVE_STATUS = new Set([
    'inactivo',
    'pendiente',
    'suspendido',
    'baja',
    'eliminado',
    'rechazado',
    'bloqueado',
]);

const isExplicitlyInactive = (data) => {
    if (!data || typeof data !== 'object') {
        return false;
    }

    if (typeof data.activo === 'boolean' && !data.activo) {
        return true;
    }

    if (typeof data.habilitado === 'boolean' && !data.habilitado) {
        return true;
    }

    const status = (data.estatus || data.status || data.state || '').toString().trim().toLowerCase();
    return status && INACTIVE_STATUS.has(status);
};

const mergeSnapshots = (snapshots) => {
    const map = new Map();
    snapshots.forEach((snapshot) => {
        snapshot.forEach((docSnap) => {
            map.set(docSnap.id, docSnap.data() || {});
        });
    });
    return map;
};

export const countEligibleVoters = async (fraccionamientoId) => {
    if (!fraccionamientoId) {
        return 0;
    }

    try {
        const usuariosRef = collection(db, 'usuarios');
        const casasCollection = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const residentsFromHouses = new Set();

        // 1) Obtener residentes desde las casas (residentesAsignados / propietariosArrendados)
        try {
            const casasSnap = await getDocs(casasCollection);
            casasSnap.forEach((casaDoc) => {
                const data = casaDoc.data() || {};

                if (Array.isArray(data.residentesAsignados)) {
                    data.residentesAsignados.forEach((res) => {
                        if (res?.usuarioId) {
                            residentsFromHouses.add(res.usuarioId);
                        }
                    });
                }

                if (Array.isArray(data.propietariosArrendados)) {
                    data.propietariosArrendados.forEach((res) => {
                        if (res?.usuarioId) {
                            residentsFromHouses.add(res.usuarioId);
                        }
                    });
                }
            });
        } catch (houseFetchError) {
            console.error('countEligibleVoters: error al leer casas', houseFetchError);
        }

        // 2) Validar que los usuarios existan, tengan rol 'user' y estén activos
        const chunks = [];
        const ids = Array.from(residentsFromHouses);
        const CHUNK_SIZE = 10; // Firestore 'in' tiene límite 10
        for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
            chunks.push(ids.slice(i, i + CHUNK_SIZE));
        }

        const validatedResidents = new Set();

        for (const chunk of chunks) {
            try {
                const qUsers = query(usuariosRef, where(documentId(), 'in', chunk));
                const snap = await getDocs(qUsers);
                snap.forEach((docSnap) => {
                    const data = docSnap.data() || {};
                    const rol = (data.rol || '').toString().toLowerCase();
                    const fraccMatch = data.fraccionamientoId === fraccionamientoId || data.fraccionamientoTd === fraccionamientoId;

                    if (rol === 'user' && fraccMatch && !isExplicitlyInactive(data)) {
                        validatedResidents.add(docSnap.id);
                    }
                });
            } catch (chunkError) {
                console.error('countEligibleVoters: error validando residentes', { chunk, error: chunkError });
            }
        }

        // 3) Si no obtuvimos nada desde casas, caer a la lógica anterior
        let total = validatedResidents.size;

        if (total === 0) {
            const snapshots = [];

            const mainQuery = query(usuariosRef, where('fraccionamientoId', '==', fraccionamientoId));
            snapshots.push(await getDocs(mainQuery));

            // Algunas colecciones históricas guardaron la relación en 'fraccionamientoTd'.
            const legacyQuery = query(usuariosRef, where('fraccionamientoTd', '==', fraccionamientoId));
            snapshots.push(await getDocs(legacyQuery));

            const uniqueUsers = mergeSnapshots(snapshots);

            uniqueUsers.forEach((data) => {
                if (isExplicitlyInactive(data)) {
                    return;
                }

                const rol = (data.rol || '').toString().toLowerCase();
                if (rol === 'user') {
                    total += 1;
                }
            });
        }

        return total;
    } catch (error) {
        console.error('countEligibleVoters: error al contar residentes elegibles', error);
        return 0;
    }
};

export default countEligibleVoters;
